/**
 * KAN-783: редактор атрибутов лида, карточка "Что бот выясняет у клиента"
 * в настройках организации. Строка на атрибут: название, подсказка модели,
 * тип, варианты (только у выбора), флажки "Показывать в CRM" и "В Требуют
 * внимания", правило ответа; кнопки добавить, удалить, выше, ниже.
 * Логика (разбор вариантов, проверка, порядок) в lib/leadAttributes.
 * Сохраняет страница настроек вместе с остальными полями организации.
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  LEAD_ATTRIBUTE_TYPES,
  RENAME_WARNING,
  moveAttribute,
  newAttribute,
  parseOptions,
  validateAttributes,
} from '@/lib/leadAttributes';
import type { LeadAttribute, LeadAttributeType } from '@/types/settings';

interface LeadAttributesEditorProps {
  attributes: LeadAttribute[];
  onChange: (attributes: LeadAttribute[]) => void;
  disabled?: boolean;
  /** Зарезервированные названия от backend; без них встроенный список. */
  reservedNames?: readonly string[];
}

const SELECT =
  'flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 disabled:cursor-not-allowed disabled:opacity-50';

const EMPTY_HINT = 'Бот спрашивает это у клиента и записывает в карточку. Добавьте первый атрибут.';

export function LeadAttributesEditor({ attributes, onChange, disabled = false, reservedNames }: LeadAttributesEditorProps) {
  // Текст поля вариантов во время ввода: "а, " не должно схлопываться в "а".
  const [optionDrafts, setOptionDrafts] = useState<Record<string, string>>({});
  const errors = validateAttributes(attributes, reservedNames);

  const patch = (index: number, updates: Partial<LeadAttribute>) =>
    onChange(attributes.map((attribute, i) => (i === index ? { ...attribute, ...updates } : attribute)));

  const remove = (index: number) => onChange(attributes.filter((_, i) => i !== index));

  const optionsText = (attribute: LeadAttribute) => optionDrafts[attribute.id] ?? attribute.options.join(', ');

  return (
    <div data-testid="lead-attributes" className="space-y-4">
      <p className="text-sm text-gray-500">{RENAME_WARNING}</p>
      {attributes.length === 0 && <p className="text-sm text-gray-600">{EMPTY_HINT}</p>}
      {attributes.map((attribute, index) => {
        const id = `lead-attr-${attribute.id}`;
        const error = errors[attribute.id];
        return (
          <div key={attribute.id} data-testid="lead-attribute-row" className="space-y-3 rounded-md border border-gray-200 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex-1 space-y-1">
                <Label htmlFor={`${id}-name`}>Название</Label>
                <Input
                  id={`${id}-name`}
                  value={attribute.name}
                  disabled={disabled}
                  placeholder="Условия закупки"
                  onChange={(e) => patch(index, { name: e.target.value })}
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>
              <div className="space-y-1 sm:w-48">
                <Label htmlFor={`${id}-type`}>Тип</Label>
                <select
                  id={`${id}-type`}
                  className={SELECT}
                  value={attribute.type}
                  disabled={disabled}
                  onChange={(e) => patch(index, { type: e.target.value as LeadAttributeType })}
                >
                  {LEAD_ATTRIBUTE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-1 sm:pt-6">
                <Button type="button" variant="ghost" size="icon" aria-label="Выше" title="Выше" disabled={disabled || index === 0} onClick={() => onChange(moveAttribute(attributes, index, -1))}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" aria-label="Ниже" title="Ниже" disabled={disabled || index === attributes.length - 1} onClick={() => onChange(moveAttribute(attributes, index, 1))}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" aria-label="Удалить" title="Удалить" className="text-gray-500 hover:text-red-600" disabled={disabled} onClick={() => remove(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor={`${id}-hint`}>Подсказка модели</Label>
              <Input
                id={`${id}-hint`}
                value={attribute.hint}
                disabled={disabled}
                placeholder="Что именно спросить или откуда взять"
                onChange={(e) => patch(index, { hint: e.target.value })}
              />
            </div>

            {attribute.type === 'choice' && (
              <div className="space-y-1">
                <Label htmlFor={`${id}-options`}>Варианты через запятую</Label>
                <Input
                  id={`${id}-options`}
                  value={optionsText(attribute)}
                  disabled={disabled}
                  placeholder="звонок, чат, почта"
                  onChange={(e) => {
                    setOptionDrafts((prev) => ({ ...prev, [attribute.id]: e.target.value }));
                    patch(index, { options: parseOptions(e.target.value) });
                  }}
                  onBlur={() =>
                    setOptionDrafts((prev) => {
                      const next = { ...prev };
                      delete next[attribute.id];
                      return next;
                    })
                  }
                />
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-crm`}
                  checked={attribute.show_in_crm}
                  disabled={disabled}
                  onCheckedChange={(checked) => patch(index, { show_in_crm: checked })}
                />
                <Label htmlFor={`${id}-crm`} className="cursor-pointer">
                  Показывать в CRM
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-attention`}
                  checked={attribute.attention}
                  disabled={disabled}
                  onCheckedChange={(checked) => patch(index, { attention: checked })}
                />
                <Label htmlFor={`${id}-attention`} className="cursor-pointer">
                  В Требуют внимания
                </Label>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor={`${id}-rule`}>Правило ответа</Label>
              <Textarea
                id={`${id}-rule`}
                rows={2}
                value={attribute.reply_rule}
                disabled={disabled}
                placeholder="Как боту отвечать, когда клиент об этом спрашивает"
                onChange={(e) => patch(index, { reply_rule: e.target.value })}
              />
            </div>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => onChange([...attributes, newAttribute()])}>
        <Plus className="mr-1 h-4 w-4" />
        Добавить атрибут
      </Button>
    </div>
  );
}
